import { useState, useRef, useEffect } from 'react';
import { MessageCircle, Send, Trash2, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import api from '@/lib/api';
import { streamChat } from '@/lib/streamChat';
import MarkdownMessage from '@/components/MarkdownMessage';

const SUGGESTIONS = [
  'Kolik kalibrací čeká na schválení?',
  'Jaký je stav objednávek?',
  'Které etalony brzy expirují?',
  'Shrň poslední schválené kalibrace',
  'Kolik přístrojů máme v evidenci?',
  'Jaké metodiky jsou ověřené?',
];

export default function ChatPage() {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    api.get('/chat/history?sessionId=fullpage').then(r => {
      if (r.data?.length) setMessages(r.data);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  useEffect(() => {
    if (inputRef.current) inputRef.current.focus();
  }, []);

  // #1: streaming odpovědí — chunky padají postupně do posledního assistant message.
  const send = async (text) => {
    const q = (text || input).trim();
    if (!q || loading) return;
    setInput('');
    setMessages(prev => [...prev, { role: 'user', content: q }, { role: 'assistant', content: '' }]);
    setLoading(true);
    await streamChat({
      message: q,
      sessionId: 'fullpage',
      onChunk: (chunk) => {
        setMessages(prev => {
          const copy = [...prev];
          const last = copy[copy.length - 1];
          if (last && last.role === 'assistant') {
            copy[copy.length - 1] = { ...last, content: last.content + chunk };
          }
          return copy;
        });
      },
      onError: (err) => {
        setMessages(prev => {
          const copy = [...prev];
          const last = copy[copy.length - 1];
          if (last && last.role === 'assistant' && !last.content) {
            copy[copy.length - 1] = { ...last, content: 'Omlouvám se, došlo k chybě: ' + err };
          }
          return copy;
        });
      },
    });
    setLoading(false);
  };

  const clearHistory = async () => {
    await api.delete('/chat/history?sessionId=fullpage').catch(() => {});
    setMessages([]);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  return (
    <div data-testid="chat-page" className="flex flex-col h-[calc(100vh-7rem)]">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="ct-h1 flex items-center gap-2">
            <MessageCircle className="w-5 h-5" style={{ color: 'var(--ct-accent)' }} />
            CalibraTech AI Asistent
          </h1>
          <p className="ct-page-subtitle">
            Dotazujte se na data z celé aplikace v přirozeném jazyce
          </p>
        </div>
        <Button
          data-testid="chat-page-clear"
          onClick={clearHistory}
          variant="outline"
          className="gap-1 text-xs"
          style={{ color: 'var(--ct-text-secondary)', borderColor: 'var(--ct-border)' }}
        >
          <Trash2 className="w-3.5 h-3.5" /> Smazat historii
        </Button>
      </div>

      {/* Messages area */}
      <div
        className="ct-panel flex-1 flex flex-col overflow-hidden"
      >
        <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-4">
          {messages.length === 0 && !loading && (
            <div className="flex flex-col items-center justify-center h-full space-y-6">
              <div className="text-center space-y-2">
                <div
                  className="w-16 h-16 rounded-full mx-auto flex items-center justify-center"
                  style={{ background: 'rgba(var(--ct-accent-rgb), 0.1)' }}
                >
                  <MessageCircle className="w-8 h-8" style={{ color: 'var(--ct-accent)' }} />
                </div>
                <h3 className="ct-h2">
                  Zeptejte se na cokoli
                </h3>
                <p className="text-sm max-w-md" style={{ color: 'var(--ct-text-secondary)' }}>
                  AI asistent má přístup ke všem datům aplikace — kalibrace, objednávky, etalony, přístroje, metodiky a další.
                </p>
              </div>
              <div className="grid grid-cols-2 gap-2 max-w-lg w-full">
                {SUGGESTIONS.map((q, i) => (
                  <button
                    key={i}
                    data-testid={`chat-suggestion-${i}`}
                    onClick={() => send(q)}
                    className="text-left text-xs px-3 py-2.5 rounded-lg transition-colors"
                    style={{
                      color: 'var(--ct-text)',
                      background: 'var(--ct-bg)',
                      border: '1px solid var(--ct-border)',
                    }}
                  >
                    {q}
                  </button>
                ))}
              </div>
            </div>
          )}

          {messages.map((msg, i) => (
            <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
              <div
                className={`max-w-[70%] px-4 py-3 rounded-xl text-sm leading-relaxed ${msg.role === 'user' ? 'whitespace-pre-wrap' : ''}`}
                style={msg.role === 'user' ? {
                  background: 'var(--ct-accent)',
                  color: '#fff',
                  borderBottomRightRadius: '4px',
                } : {
                  background: 'var(--ct-bg)',
                  color: 'var(--ct-text)',
                  border: '1px solid var(--ct-border)',
                  borderBottomLeftRadius: '4px',
                }}
              >
                {msg.role === 'user' ? msg.content : <MarkdownMessage content={msg.content} />}
              </div>
            </div>
          ))}

          {loading && (
            <div className="flex justify-start">
              <div
                className="px-4 py-3 rounded-xl text-sm flex items-center gap-2"
                style={{ background: 'var(--ct-bg)', border: '1px solid var(--ct-border)', color: 'var(--ct-text-secondary)' }}
              >
                <Loader2 className="w-4 h-4 animate-spin" style={{ color: 'var(--ct-accent)' }} />
                Analyzuji data a připravuji odpověď...
              </div>
            </div>
          )}
        </div>

        {/* Input bar */}
        <div
          className="flex items-center gap-3 px-4 py-3 flex-shrink-0"
          style={{ borderTop: '1px solid var(--ct-border)' }}
        >
          <input
            ref={inputRef}
            data-testid="chat-page-input"
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Napište svůj dotaz..."
            className="flex-1 bg-transparent text-sm outline-none py-1"
            style={{ color: 'var(--ct-text)' }}
            disabled={loading}
          />
          <button
            data-testid="chat-page-send"
            onClick={() => send()}
            disabled={!input.trim() || loading}
            className="px-4 py-2 rounded-lg text-sm font-medium transition-all disabled:opacity-30 flex items-center gap-2"
            style={{ background: 'var(--ct-accent)', color: '#fff' }}
          >
            <Send className="w-4 h-4" />
            Odeslat
          </button>
        </div>
      </div>
    </div>
  );
}
